/**
 * Panel 4 — `Teams Coverage` (§A5, Matrix 3): the team-exposure fairness check.
 *
 * An official covers **both** teams of any match they are appointed to, so each appointment
 * adds two counts and a row total is twice that official's appointments. That is not an
 * oddity to be corrected — it is what the table measures, and the foot says so, because a
 * reader who works it out for himself has already half-decided the table is wrong.
 *
 * §A4 rule 5 raises an advisory above the configured heat threshold; the shading here is the
 * surface that lets the TD see the drift building before it crosses it.
 */

import type { CompetitionFilter, RoleFilter, TeamsTable } from "./derive.js";
import { competitionLabel, heatClass } from "./derive.js";
import { COMPETITION_TABS, ROLE_TABS, Tabs } from "./Tabs.js";
import { cellClass } from "./useCellFlash.js";

export interface TeamsCoveragePanelProps {
  table: TeamsTable;
  competition: CompetitionFilter;
  onCompetition: (value: CompetitionFilter) => void;
  role: RoleFilter;
  onRole: (value: RoleFilter) => void;
  flashing: ReadonlySet<string>;
  /**
   * RUN 37 — what the counts are counted OVER, in words, for the footnote.
   *
   * The table used to be one day's, and its footnote said so. It is now the whole schedule's,
   * because a Technical Delegate ran `Draft The Pool Stage`, saw ten days written, and read a
   * footnote telling him six matches had been reflected. The span is passed in rather than
   * inferred here: this panel counts what it is handed and must never be the second opinion on
   * what that was. The default is the old wording, so a caller that still hands it one day
   * still says `on this day`.
   */
  span?: string;
}

export function TeamsCoveragePanel({
  table,
  competition,
  onCompetition,
  role,
  onRole,
  flashing,
  span = "on this day",
}: TeamsCoveragePanelProps): React.JSX.Element {
  const { teams, rows } = table;

  return (
    <section
      className="panel panel--d4"
      aria-labelledby="teamsTitle"
      data-howto="teams-coverage-panel"
    >
      <div className="panel__head">
        <h3 className="panel__title" id="teamsTitle">
          Teams Coverage
        </h3>
        <span className="panel__note">Team-Exposure Fairness Check</span>
        <span className="spacer" />
        <span className="filtlab">Competition</span>
        <Tabs
          label="Teams coverage competition filter"
          options={COMPETITION_TABS}
          value={competition}
          onChange={onCompetition}
          tint="competition"
        />
        <span className="filtlab">Officials</span>
        <Tabs
          label="Teams coverage officials filter"
          options={ROLE_TABS}
          value={role}
          onChange={onRole}
        />
      </div>

      <div className="matwrap tfwrap">
        <table
          className="mattable tftable"
          aria-label="Teams coverage — matches involving each team per official"
        >
          <thead>
            <tr className="matgrp">
              <th scope="col">Officials</th>
              <th scope="col" colSpan={teams.length + 1} className="g-right">
                Matches Involving Each Team
              </th>
            </tr>
            <tr>
              <th scope="col" className="c-name">
                Official
              </th>
              {teams.map((team, index) => (
                <th key={team} scope="col" className={index === 0 ? "first-right" : undefined}>
                  {team}
                </th>
              ))}
              <th scope="col" className="c-tot">
                Total
              </th>
            </tr>
          </thead>

          <tbody>
            {rows.map((row) => {
              const id = row.official.tmsPersonId;
              return (
                <tr
                  key={id}
                  className={row.official.role === "TO" ? "is-to" : undefined}
                  data-testid={`coverage-${id}`}
                >
                  <td
                    className={`c-name${row.official.gender === null ? "" : ` g-${row.official.gender}`}`}
                  >
                    <span className="rolechip">
                      {row.official.role === "TO" ? "TO" : row.official.role === "ATD" ? "ATD" : "JG"}
                    </span>
                    {row.official.surname}
                    <span className="nat num">{row.official.country}</span>
                  </td>
                  {teams.map((team, index) => {
                    const value = row.per[team] ?? 0;
                    return (
                      <td
                        key={team}
                        className={cellClass(
                          `${heatClass(value)}${index === 0 ? " first-right" : ""}`,
                          `f:${id}:${team}`,
                          flashing,
                        )}
                      >
                        {value}
                      </td>
                    );
                  })}
                  <td className={cellClass("c-tot", `f:${id}:tot`, flashing)}>{row.total}</td>
                </tr>
              );
            })}
          </tbody>

          <tfoot>
            <tr>
              <td className="c-name">Total</td>
              {teams.map((team, index) => (
                <td key={team} className={index === 0 ? "first-right" : undefined}>
                  {table.columnTotals[team] ?? 0}
                </td>
              ))}
              <td>{table.grandTotal}</td>
            </tr>
          </tfoot>
        </table>

        {teams.length === 0 ? (
          <div className="emptystate">
            <b>No teams in this view.</b>
            Coverage appears as soon as the day carries a match in this competition.
          </div>
        ) : null}
      </div>

      <div className="panel__foot">
        <div className="matlegend" aria-label="Team exposure heat key">
          <span>Team Exposure</span>
          <span className="hsw">
            <i className="k0" aria-hidden="true" />0
          </span>
          <span className="hsw">
            <i className="k1" aria-hidden="true" />1
          </span>
          <span className="hsw">
            <i className="k2" aria-hidden="true" />2
          </span>
          <span className="hsw">
            <i className="k3" aria-hidden="true" />
            3+
          </span>
        </div>
        <b>Reading the table —</b> an official covers <b>both</b> teams of any match they are
        appointed to, so each appointment adds two counts and the row total is twice their
        appointments. Use it to spot an official drawing the same team repeatedly.{" "}
        <span data-testid="coverage-scope">
          Currently reflecting <b>{table.fixtures}</b>{" "}
          {competition === "all" ? "" : `${competitionLabel(competition)} `}
          {table.fixtures === 1 ? "match" : "matches"} {span}, covering{" "}
          <b>{teams.length}</b> {teams.length === 1 ? "team" : "teams"}.
        </span>
      </div>
    </section>
  );
}
